/**
 * Utility for Excel template observability: metrics, structured logging,
 * and error reporting via NewRelic Browser Agent.
 *
 * Metrics recorded as PageActions:
 *   - excel_generation_duration_ms   – Time to generate the download stream.
 *   - excel_parsing_duration_ms      – Time to parse an upload.
 *   - upload_row_count               – Total rows processed per upload.
 *   - validation_error_rate          – Percentage of uploads with validation errors.
 *   - memory_usage_mb                – Heap usage during file processing.
 *
 * Structured logs include FileSizeBytes, RowCount, account-level info, and
 * exclude PII / order contents.
 */
import { useNewRelic } from '@/composables/useNewRelic';

type NewRelicClient = ReturnType<typeof useNewRelic>;

function recordMetric(...args: Parameters<NewRelicClient['addPageAction']>) {
  return useNewRelic().addPageAction(...args);
}

function log(...args: Parameters<NewRelicClient['log']>) {
  return useNewRelic().log(...args);
}

function noticeError(...args: Parameters<NewRelicClient['noticeError']>) {
  return useNewRelic().noticeError(...args);
}

function getHeapUsageMb(): number | null {
  const perf = performance as Performance & {
    memory?: { usedJSHeapSize: number };
  };
  if (perf.memory) {
    return Math.round((perf.memory.usedJSHeapSize / 1024 / 1024) * 100) / 100;
  }

  return null;
}

// -------------------------------------------------------------------------
// Download (generation) observability
// -------------------------------------------------------------------------

export function recordGenerationMetrics(params: {
  durationMs: number;
  accountId: string;
  accountName?: string;
  legalEntity?: string;
  productCount: number;
}) {
  const memoryMb = getHeapUsageMb();

  recordMetric('excel_generation_duration_ms', {
    durationMs: params.durationMs,
    accountId: params.accountId,
    accountName: params.accountName,
    legalEntity: params.legalEntity,
    productCount: params.productCount,
  });

  if (memoryMb !== null) {
    recordMetric('memory_usage_mb', {
      memoryMb,
      operation: 'generation',
      accountId: params.accountId,
    });
  }

  log(
    `Excel template generated: accountId=${params.accountId}, productCount=${params.productCount}, durationMs=${params.durationMs}`,
    {
      level: 'INFO',
      customAttributes: {
        feature: 'ExcelTemplateGeneration',
        accountId: params.accountId,
        accountName: params.accountName,
        legalEntity: params.legalEntity,
        productCount: params.productCount,
        durationMs: params.durationMs,
        memoryMb,
      },
    },
  );
}

// -------------------------------------------------------------------------
// Upload (parsing + validation) observability
// -------------------------------------------------------------------------

export function recordParsingMetrics(params: {
  durationMs: number;
  fileSizeBytes: number;
  rowCount: number;
  accountId: string;
  accountName?: string;
  legalEntity?: string;
}) {
  const memoryMb = getHeapUsageMb();

  recordMetric('excel_parsing_duration_ms', {
    durationMs: params.durationMs,
    fileSizeBytes: params.fileSizeBytes,
    rowCount: params.rowCount,
    accountId: params.accountId,
  });

  recordMetric('upload_row_count', {
    rowCount: params.rowCount,
    accountId: params.accountId,
  });

  if (memoryMb !== null) {
    recordMetric('memory_usage_mb', {
      memoryMb,
      operation: 'parsing',
      accountId: params.accountId,
    });
  }

  log(
    `Excel upload parsed: accountId=${params.accountId}, fileSizeBytes=${params.fileSizeBytes}, rowCount=${params.rowCount}, durationMs=${params.durationMs}`,
    {
      level: 'INFO',
      customAttributes: {
        feature: 'ExcelTemplateUpload',
        accountId: params.accountId,
        accountName: params.accountName,
        legalEntity: params.legalEntity,
        fileSizeBytes: params.fileSizeBytes,
        rowCount: params.rowCount,
        durationMs: params.durationMs,
        memoryMb,
      },
    },
  );
}

export function recordValidationMetrics(params: {
  totalRows: number;
  failedRows: number;
  accountId: string;
  accountName?: string;
  legalEntity?: string;
}) {
  const successfulRows = params.totalRows - params.failedRows;
  const errorRate = params.totalRows > 0 ? Math.round((params.failedRows / params.totalRows) * 10000) / 100 : 0;

  recordMetric('validation_error_rate', {
    errorRate,
    totalRows: params.totalRows,
    failedRows: params.failedRows,
    successfulRows,
    accountId: params.accountId,
  });

  log(
    `Excel upload validated: accountId=${params.accountId}, totalRows=${params.totalRows}, failedRows=${params.failedRows}, errorRate=${errorRate}%`,
    {
      level: params.failedRows > 0 ? 'WARN' : 'INFO',
      customAttributes: {
        feature: 'ExcelTemplateUploadValidation',
        accountId: params.accountId,
        accountName: params.accountName,
        legalEntity: params.legalEntity,
        totalRows: params.totalRows,
        failedRows: params.failedRows,
        successfulRows,
        errorRate,
      },
    },
  );
}

// -------------------------------------------------------------------------
// Error logging
// -------------------------------------------------------------------------

export function recordUploadError(params: {
  error: Error;
  accountId: string;
  accountName?: string;
  legalEntity?: string;
  fileSizeBytes?: number;
  context?: string;
}) {
  const baseAttrs = {
    feature: 'ExcelTemplateUpload',
    accountId: params.accountId,
    accountName: params.accountName,
    legalEntity: params.legalEntity,
    fileSizeBytes: params.fileSizeBytes,
    context: params.context,
  };

  noticeError(params.error, baseAttrs);

  log(`Excel upload error: ${params.error.message}`, {
    level: 'ERROR',
    customAttributes: {
      ...baseAttrs,
      errorName: params.error.name,
      errorStack: params.error.stack,
    },
  });
}

export function recordGenerationError(params: {
  error: Error;
  accountId: string;
  accountName?: string;
  legalEntity?: string;
}) {
  const baseAttrs = {
    feature: 'ExcelTemplateGeneration',
    accountId: params.accountId,
    accountName: params.accountName,
    legalEntity: params.legalEntity,
  };

  noticeError(params.error, baseAttrs);

  log(`Excel generation error: ${params.error.message}`, {
    level: 'ERROR',
    customAttributes: {
      ...baseAttrs,
      errorName: params.error.name,
      errorStack: params.error.stack,
    },
  });
}
